import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { isAdminPreferencePath } from '@/lib/menuPermissions.core';

interface RoleRouteProps {
  allowed: Array<'ADMIN' | 'USER' | 'AGENT' | 'RIDER'>;
  children: React.ReactNode;
  fallback?: string; // path
}

export function RoleRoute({ allowed, children, fallback = '/home/inbox' }: RoleRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets();
  if (loading) return null;
  if (pendingAssetsLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const roleRaw = (user.role || '').toString().toUpperCase();
  const isSuperAdmin = (user as any)?.is_super_admin;
  const isOwner = (user as any)?.is_organization_owner;
  const effectiveRole = (roleRaw === 'ADMIN' || isSuperAdmin || isOwner) ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : (roleRaw === 'RIDER' ? 'RIDER' : 'USER'));

  // Custom menu permissions let an admin grant a member access to feature pages
  // beyond their default role (CRM, Marketing posts, Automation, Commerce, …) —
  // many of which are themselves coded as allowed=['ADMIN']. The bypass must
  // keep working for those. It must NEVER unlock the Account-Settings admin
  // *preferences* (Audit Log, Billing, Integrations, Business/Social Accounts,
  // Team Management), which stay strictly ADMIN-only. We key that off an
  // explicit path list — NOT a generic allowed===['ADMIN'] check, which would
  // wrongly hard-lock every ['ADMIN'] feature page (the bug that broke CRM and
  // the Social/LinkedIn posts pages for agents who'd been granted them).
  const hasCustomMenuPermissions = Array.isArray((user as any)?.menu_permissions);
  const isAdminPreference = isAdminPreferencePath(location.pathname);
  const customPermsBypass = hasCustomMenuPermissions && !isAdminPreference;
  const isAutomationRoute = location.pathname.startsWith('/home/automation');
  const allowOnboardingAutomationAccess =
    isAutomationRoute && (!!pendingAssets?.pending_flow || !!pendingAssets?.activated_business_account_id);

  if (!customPermsBypass && !allowed.includes(effectiveRole) && !allowOnboardingAutomationAccess) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}
